// Client Executive Summary — Build v1 preview & feedback.
//
// Tom Clancy locked Build v1 on 2026-06-30: an 8-page client-facing
// executive summary deck that accompanies (does not replace) the full
// D-Tools proposals. The 4 East 66th project is the pilot.
//
// This page lets the sales team and proposal team:
//   - View the locked sample deck (PDF embedded + page-by-page breakdown)
//   - Read the structure / SOP so they know what to provide
//   - Submit feedback that flows back to Tom
//
// Access mirrors Proposal Review (Mgmt + Sales + Finance + Dawn + Silvia).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Divider, Grid, IconButton, MenuItem, Paper, Select, Stack,
  TextField, Tooltip, Typography, FormControl, InputLabel,
} from '@mui/material';
import {
  Article as DeckIcon,
  Download as DownloadIcon,
  OpenInNew as OpenIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Verified as LockedIcon,
  Forum as FeedbackIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const NAVY = '#1A5490';
const PEACH = '#FF6B35';

function formatTimestamp(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function BuildV1Preview() {
  const [build, setBuild]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const [feedback, setFeedback]       = useState([]);
  const [discipline, setDiscipline]   = useState('');
  const [pageRef, setPageRef]         = useState('');
  const [body, setBody]               = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const loadBuild = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/exec-summary/build');
      setBuild(r.data);
    } catch (e) {
      console.error(e);
      setError('Failed to load build details. Refresh to try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFeedback = useCallback(async () => {
    try {
      const r = await api.get('/exec-summary/feedback');
      setFeedback(r.data.items || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { loadBuild(); loadFeedback(); }, [loadBuild, loadFeedback]);

  const submitFeedback = async () => {
    if (!body.trim()) {
      setSubmitError('Please enter your feedback before sending.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.post('/exec-summary/feedback', {
        discipline: discipline || null,
        page:       pageRef    || null,
        body:       body.trim(),
      });
      setBody(''); setDiscipline(''); setPageRef('');
      await loadFeedback();
    } catch (e) {
      console.error(e);
      setSubmitError(e?.response?.data?.error || 'Failed to send feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  // Alphabetize page list under each heading per Tom's standing rule.
  // The deck pages have a fixed reading order, but disciplines and open items
  // are alphabetized below.
  const sortedDisciplines = useMemo(() => {
    if (!build?.structure?.discipline_order) return [];
    return [...build.structure.discipline_order].sort((a, b) => a.localeCompare(b));
  }, [build]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !build) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button onClick={loadBuild} startIcon={<RefreshIcon />}>Retry</Button>
        }>{error || 'No build data.'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* ── Header ─────────────────────────────────────────────── */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
            <DeckIcon sx={{ color: NAVY }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: NAVY, letterSpacing: '-0.01em' }}>
              Client Executive Summary
            </Typography>
            <Chip
              icon={<LockedIcon sx={{ color: `${NAVY} !important` }} />}
              label={`Build ${build.build}`}
              size="small"
              sx={{ bgcolor: 'rgba(17,68,122,0.08)', color: NAVY, fontWeight: 600, letterSpacing: '0.05em' }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Locked {build.locked} · Approved by {build.approved_by} · Pilot: {build.sample_project}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<OpenIcon />}
            href={build.pdf_url}
            target="_blank"
            rel="noopener"
          >
            Open PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            href={build.pdf_url}
            download
            sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#0d3661' } }}
          >
            Download
          </Button>
        </Stack>
      </Stack>

      {/* ── What this is ───────────────────────────────────────── */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        A short, client-facing deck that ships <em>with</em> every D-Tools proposal — not as a replacement.
        It tells the client what they're getting and where, in a luxury register, before they read the full scope.
        Each discipline is capped at <strong>2 pages</strong>, project-size dependent.
      </Alert>

      <Grid container spacing={3}>
        {/* ── Left: Embedded PDF preview ───────────────────────── */}
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.15em', fontWeight: 600 }}>
                Live preview — {build.sample_project}
              </Typography>
              <Typography variant="caption" color="text.secondary">8 pages · Letter</Typography>
            </Box>
            <Box sx={{ height: { xs: 600, md: 900 }, bgcolor: '#f5f5f7' }}>
              <iframe
                title="Client Executive Summary — Build v1"
                src={build.pdf_url}
                style={{ width: '100%', height: '100%', border: 0 }}
              />
            </Box>
          </Paper>

          {/* Per-page breakdown */}
          <Typography variant="h6" sx={{ mt: 4, mb: 1.5, color: NAVY, fontWeight: 600 }}>
            Page-by-page
          </Typography>
          <Grid container spacing={2}>
            {build.pages.map((p) => (
              <Grid item xs={12} sm={6} key={p.num}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <Box sx={{ aspectRatio: '8.5 / 11', bgcolor: '#f5f5f7', overflow: 'hidden', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <img
                      src={p.thumb}
                      alt={`Page ${p.num} — ${p.title}`}
                      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                    />
                  </Box>
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
                      <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.12em', fontWeight: 600 }}>
                        Page {p.num}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {p.title}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">{p.notes}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* ── Right: SOP + feedback ────────────────────────────── */}
        <Grid item xs={12} md={5}>
          {/* SOP — what we do now */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
            <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.18em', fontWeight: 600 }}>
              How this works today
            </Typography>
            <Box sx={{ width: 36, height: 2, bgcolor: PEACH, my: 1 }} />
            <Stack spacing={1.2} sx={{ mt: 1.5 }}>
              {build.workflow_now.map((step, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ minWidth: 22, height: 22, borderRadius: '50%', bgcolor: NAVY, color: 'white', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.2 }}>
                    {i + 1}
                  </Box>
                  <Typography variant="body2">{step}</Typography>
                </Stack>
              ))}
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              <strong>Builds the deck:</strong> {build.owners.builds_deck.join(' & ')} (Proposal Team).
              Sales teams provide proposal refs, subtotals, and a short narrative per discipline.
              The proposal team produces the final PDF and returns it to the salesperson.
            </Typography>
          </Paper>

          {/* SOP — target workflow */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, bgcolor: 'rgba(241,168,108,0.05)' }}>
            <Typography variant="overline" sx={{ color: PEACH, letterSpacing: '0.18em', fontWeight: 600 }}>
              Target workflow — coming with v2
            </Typography>
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {build.workflow_target.map((step, i) => (
                <Typography key={i} variant="body2" color="text.secondary">
                  · {step}
                </Typography>
              ))}
            </Stack>
          </Paper>

          {/* Open items */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
            <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.18em', fontWeight: 600 }}>
              Open questions for your feedback
            </Typography>
            <Box sx={{ width: 36, height: 2, bgcolor: PEACH, my: 1 }} />
            <Stack spacing={1.2} sx={{ mt: 1.5 }}>
              {build.open_items.slice().sort((a, b) => a.localeCompare(b)).map((q, i) => (
                <Typography key={i} variant="body2">· {q}</Typography>
              ))}
            </Stack>
          </Paper>

          {/* Feedback form */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <FeedbackIcon sx={{ color: NAVY }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: NAVY }}>
                Send feedback to Tom
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Everything submitted here is visible to Tom and consolidated into the v2 spec.
            </Typography>

            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="esum-discipline-label">Discipline (optional)</InputLabel>
                  <Select
                    labelId="esum-discipline-label"
                    label="Discipline (optional)"
                    value={discipline}
                    onChange={(e) => setDiscipline(e.target.value)}
                  >
                    <MenuItem value=""><em>—</em></MenuItem>
                    {sortedDisciplines.map((d) => (
                      <MenuItem key={d} value={d}>{d}</MenuItem>
                    ))}
                    <MenuItem value="Cover">Cover</MenuItem>
                    <MenuItem value="About / Next Step">About / Next Step</MenuItem>
                    <MenuItem value="Overall">Overall</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small"
                  label="Page # (optional)"
                  value={pageRef}
                  onChange={(e) => setPageRef(e.target.value)}
                  placeholder="e.g. 3"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth multiline minRows={4}
                  label="Your feedback"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="What works, what to change, what's missing."
                />
              </Grid>
              {submitError && (
                <Grid item xs={12}>
                  <Alert severity="error">{submitError}</Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={submitFeedback}
                    disabled={submitting || !body.trim()}
                    startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
                    sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#0d3661' } }}
                  >
                    {submitting ? 'Sending…' : 'Send feedback'}
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            {feedback.length > 0 && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.15em', fontWeight: 600 }}>
                  Recent feedback
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1.5, maxHeight: 320, overflowY: 'auto' }}>
                  {feedback.map((f) => (
                    <Box key={f.id} sx={{ p: 1.5, bgcolor: '#f7f8fa', borderRadius: 1, borderLeft: `3px solid ${NAVY}` }}>
                      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.user_name}</Typography>
                        {f.discipline && <Chip size="small" label={f.discipline} sx={{ height: 18, fontSize: 10 }} />}
                        {f.page && <Chip size="small" label={`Page ${f.page}`} sx={{ height: 18, fontSize: 10 }} />}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {formatTimestamp(f.created_at)}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{f.body}</Typography>
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
